import { useEffect, useRef, useMemo } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Environment, Lightformer, ContactShadows, Float } from '@react-three/drei';

/* ─── 5 Architectural Masterpieces representing the 5 Core Values ─── */
// Values: 0: Integrity, 1: Quality, 2: Innovation, 3: Customer First, 4: Sustainability

/* Responsive Camera Controller with mouse parallax */
const InteractiveCamera = ({ activeIndex }) => {
  const { camera, size } = useThree();
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      mouse.current.x = (e.clientX / size.width - 0.5) * 0.8;
      mouse.current.y = (e.clientY / size.height - 0.5) * 0.8;
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [size]);

  useFrame((state, delta) => {
    const isMobile = window.innerWidth < 768;
    const baseFov = isMobile ? 48 : 36;
    
    // Dynamic camera position per active value for cinematic angle shifts
    const angles = [
      { x: 0, y: 4.8, z: 9.8, rotY: 0 },
      { x: 1.2, y: 5.2, z: 9.2, rotY: -0.2 },
      { x: -1.4, y: 4.5, z: 9.5, rotY: 0.25 },
      { x: 0.8, y: 5.6, z: 9.0, rotY: -0.15 },
      { x: 0, y: 4.6, z: 10.2, rotY: 0.1 },
    ];

    const target = angles[activeIndex] || angles[0];
    const targetX = target.x + (isMobile ? 0 : mouse.current.x * 1.5);
    const targetY = (isMobile ? target.y + 0.8 : target.y) - mouse.current.y * 1.2;
    const targetZ = isMobile ? target.z + 2.8 : target.z;

    camera.position.x = THREE.MathUtils.damp(camera.position.x, targetX, 3, delta);
    camera.position.y = THREE.MathUtils.damp(camera.position.y, targetY, 3, delta);
    camera.position.z = THREE.MathUtils.damp(camera.position.z, targetZ, 3, delta);

    camera.fov = THREE.MathUtils.damp(camera.fov, baseFov, 3, delta);
    camera.lookAt(0, 1.6, 0);
    camera.updateProjectionMatrix();
  });

  return null;
};

/* ─── 1. Central Luminous Crystal Monolith ─── */
const CrystalMonolith = ({ activeIndex }) => {
  const meshRef = useRef();
  const innerRef = useRef();
  const ringRef = useRef();

  useFrame((state, delta) => {
    if (!meshRef.current) return;
    const t = state.clock.elapsedTime;

    // Continuous rotation
    meshRef.current.rotation.y = t * 0.25;
    if (ringRef.current) {
      ringRef.current.rotation.z = -t * 0.4;
      ringRef.current.rotation.x = Math.sin(t * 0.5) * 0.2;
    }

    // Emissive intensity pulsing
    if (innerRef.current) {
      const isIntegrity = activeIndex === 0;
      const targetEmissive = isIntegrity ? 3.5 : 1.2;
      innerRef.current.emissiveIntensity = THREE.MathUtils.damp(
        innerRef.current.emissiveIntensity,
        targetEmissive + Math.sin(t * 2) * 0.4,
        4,
        delta
      );
    }
  });

  return (
    <group position={[0, 1.8, 0]}>
      {/* Outer Sapphire & Glass Facade */}
      <mesh ref={meshRef}>
        <octahedronGeometry args={[1.3, 0]} />
        <meshPhysicalMaterial
          color="#1E3A7B"
          emissive="#C9971B"
          emissiveIntensity={0.15}
          metalness={0.8}
          roughness={0.08}
          clearcoat={1.0}
          clearcoatRoughness={0.05}
          transmission={0.4}
          thickness={1.2}
          transparent
          opacity={0.85}
        />
      </mesh>

      {/* Radiant Golden Inner Energy Core */}
      <mesh ref={innerRef}>
        <octahedronGeometry args={[0.75, 0]} />
        <meshPhysicalMaterial
          color="#FFD700"
          emissive="#F5C542"
          emissiveIntensity={1.8}
          metalness={0.95}
          roughness={0.02}
        />
      </mesh>

      {/* Floating Orbital Gold Ring */}
      <mesh ref={ringRef}>
        <torusGeometry args={[1.85, 0.035, 16, 64]} />
        <meshPhysicalMaterial
          color="#C9971B"
          emissive="#FFD700"
          emissiveIntensity={0.8}
          metalness={0.98}
          roughness={0.05}
        />
      </mesh>
    </group>
  );
};

/* ─── 2. Surrounding Architectural Glass & Gold Towers ─── */
const SurroundTowers = ({ activeIndex, progressRef }) => {
  const groupRef = useRef();

  const towers = useMemo(() => {
    return [
      { id: 1, pos: [-2.2, 0, -1.2], scale: [0.7, 2.2, 0.7], color: '#1E3A7B' },
      { id: 2, pos: [2.2, 0, -1.0], scale: [0.85, 2.8, 0.85], color: '#F8FAFC' },
      { id: 3, pos: [-1.8, 0, 1.5], scale: [0.65, 1.9, 0.65], color: '#F8FAFC' },
      { id: 4, pos: [1.9, 0, 1.4], scale: [0.75, 2.4, 0.75], color: '#1E3A7B' },
      { id: 5, pos: [0, 0, -2.5], scale: [1.1, 3.4, 1.1], color: '#1E3A7B' },
    ];
  }, []);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    const t = state.clock.elapsedTime;
    const p = progressRef ? progressRef.current : 0;
    
    // Slow rotational float
    groupRef.current.rotation.y = THREE.MathUtils.damp(
      groupRef.current.rotation.y,
      p * Math.PI * 0.8 + Math.sin(t * 0.3) * 0.1,
      2,
      delta
    );
  });

  return (
    <group ref={groupRef}>
      {towers.map((tw, idx) => {
        const isHighlight = activeIndex === idx;
        const targetY = tw.scale[1] / 2 + (isHighlight ? 0.3 : 0);
        return (
          <group key={tw.id} position={[tw.pos[0], targetY, tw.pos[2]]}>
            {/* Main Tower Structure */}
            <mesh>
              <boxGeometry args={tw.scale} />
              <meshPhysicalMaterial
                color={tw.color}
                metalness={0.7}
                roughness={0.1}
                clearcoat={0.8}
                transparent
                opacity={0.88}
                emissive={isHighlight ? '#C9971B' : '#000000'}
                emissiveIntensity={isHighlight ? 0.6 : 0}
              />
            </mesh>

            {/* Brushed Gold Framing Pillars */}
            <mesh position={[tw.scale[0] / 2 + 0.01, 0, tw.scale[2] / 2 + 0.01]}>
              <boxGeometry args={[0.03, tw.scale[1], 0.03]} />
              <meshPhysicalMaterial color="#C9971B" metalness={0.95} roughness={0.1} />
            </mesh>
            <mesh position={[-tw.scale[0] / 2 - 0.01, 0, -tw.scale[2] / 2 - 0.01]}>
              <boxGeometry args={[0.03, tw.scale[1], 0.03]} />
              <meshPhysicalMaterial color="#C9971B" metalness={0.95} roughness={0.1} />
            </mesh>

            {/* Glowing Accent Ring on top */}
            <mesh position={[0, tw.scale[1] / 2 + 0.04, 0]}>
              <boxGeometry args={[tw.scale[0] + 0.06, 0.06, tw.scale[2] + 0.06]} />
              <meshPhysicalMaterial
                color="#C9971B"
                emissive="#FFD700"
                emissiveIntensity={isHighlight ? 1.5 : 0.3}
                metalness={0.98}
              />
            </mesh>
          </group>
        );
      })}
    </group>
  );
};

/* ─── 3. Holographic Blueprint Grid & Levitating Structural Ribbons ─── */
const HolographicRibbons = ({ activeIndex }) => {
  const ribbon1 = useRef();
  const ribbon2 = useRef();

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (ribbon1.current) {
      ribbon1.current.rotation.y = t * 0.4;
      ribbon1.current.rotation.x = Math.sin(t * 0.3) * 0.15;
    }
    if (ribbon2.current) {
      ribbon2.current.rotation.y = -t * 0.3;
      ribbon2.current.rotation.z = Math.cos(t * 0.4) * 0.2;
    }
  });

  const getRibbonColor = () => {
    switch (activeIndex) {
      case 0: return '#FFD700'; // Integrity: Pure Gold
      case 1: return '#38BDF8'; // Quality: Sapphire Cyan
      case 2: return '#F43F5E'; // Innovation: Radiant Rose
      case 3: return '#F59E0B'; // Customer: Warm Amber
      case 4: return '#10B981'; // Sustainability: Emerald Green
      default: return '#C9971B';
    }
  };

  const currentColor = getRibbonColor();

  return (
    <group position={[0, 1.8, 0]}>
      {/* Outer Holographic Helix Ring 1 */}
      <mesh ref={ribbon1}>
        <torusGeometry args={[2.5, 0.02, 16, 100]} />
        <meshBasicMaterial color={currentColor} transparent opacity={0.65} wireframe />
      </mesh>

      {/* Outer Holographic Helix Ring 2 */}
      <mesh ref={ribbon2}>
        <torusGeometry args={[3.1, 0.015, 16, 100]} />
        <meshBasicMaterial color={currentColor} transparent opacity={0.45} />
      </mesh>
    </group>
  );
};

/* ─── 4. Shimmering Gold & Sapphire Dust Particles ─── */
const GoldSapphireParticles = ({ count = 120 }) => {
  const points = useRef();
  const { positions, speeds, colors } = useMemo(() => {
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    const spd = new Float32Array(count);
    const palette = [
      new THREE.Color('#FFD700'), // Radiant Gold
      new THREE.Color('#C9971B'), // Brand Gold
      new THREE.Color('#38BDF8'), // Sapphire Teal
      new THREE.Color('#34D399'), // Emerald Mint
    ];

    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 16;
      pos[i * 3 + 1] = Math.random() * 8;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 12;
      spd[i] = 0.2 + Math.random() * 0.5;

      const c = palette[i % 4];
      col[i * 3] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }
    return { positions: pos, speeds: spd, colors: col };
  }, [count]);

  useFrame((state) => {
    if (!points.current) return;
    const t = state.clock.elapsedTime;
    const pos = points.current.geometry.attributes.position.array;
    for (let i = 0; i < count; i++) {
      pos[i * 3 + 1] = (pos[i * 3 + 1] + speeds[i] * 0.006) % 8;
      pos[i * 3] += Math.sin(t * 0.5 + i * 0.4) * 0.001;
    }
    points.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
        <bufferAttribute attach="attributes-color" count={count} array={colors} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.06} transparent opacity={0.8} sizeAttenuation vertexColors />
    </points>
  );
};

/* ─── Main Masterplan Scene Controller ─── */
const MasterplanScene = ({ progressRef, activeIndex }) => {
  const group = useRef();

  useFrame((state, delta) => {
    if (!group.current) return;
    const p = progressRef ? progressRef.current : 0;
    const targetY = (p - 0.5) * 0.3;
    group.current.rotation.y = THREE.MathUtils.damp(group.current.rotation.y, targetY, 2.5, delta);
  });

  return (
    <group ref={group} position={[0, -0.4, 0]}>
      <CrystalMonolith activeIndex={activeIndex} />
      <SurroundTowers activeIndex={activeIndex} progressRef={progressRef} />
      <HolographicRibbons activeIndex={activeIndex} />
      <GoldSapphireParticles />
    </group>
  );
};

/* ─── Main Exported 3D Canvas Viewport ─── */
export const ValueBlocksScene = ({ progressRef, activeIndex, dpr }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const effectiveDpr = isMobile ? Math.min(typeof dpr === 'number' ? dpr : 1, 1.2) : dpr;

  return (
    <div className="relative w-full h-full bg-[#FAFBFD]">
      <Canvas
        dpr={effectiveDpr}
        camera={{ position: [0, 4.8, 9.8], fov: 36 }}
        gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
      >
        <color attach="background" args={['#FAFBFD']} />
        <InteractiveCamera activeIndex={activeIndex} />
        <fog attach="fog" args={['#FAFBFD', 10, 26]} />

        {/* Rich Architectural Lighting */}
        <ambientLight intensity={1.9} color="#FFFFFF" />
        <directionalLight position={[10, 16, 8]} intensity={3.8} color="#FFF9E6" castShadow />
        <pointLight position={[-8, 8, -6]} intensity={3.0} color="#1E3A7B" />
        <pointLight position={[7, 5, 7]} intensity={3.5} color="#C9971B" />
        <pointLight position={[0, 10, 0]} intensity={2.0} color="#FFE066" />

        <Float speed={1.2} rotationIntensity={0.03} floatIntensity={0.05}>
          <MasterplanScene progressRef={progressRef} activeIndex={activeIndex} />
        </Float>

        {/* Crisp Shadow & Architectural Grid Floor */}
        <ContactShadows
          position={[0, -0.42, 0]}
          opacity={isMobile ? 0.35 : 0.52}
          scale={28}
          blur={isMobile ? 1.2 : 2.2}
          far={8}
          color="#1E3A7B"
        />
        <gridHelper
          args={[36, 72, '#1E3A7B', '#C9971B']}
          position={[0, -0.43, 0]}
          material-opacity={0.18}
          material-transparent
        />

        {/* Studio Environment Reflections */}
        <Environment resolution={isMobile ? 64 : 128}>
          <group rotation={[Math.PI / 2, 0, 0]}>
            <Lightformer intensity={6} position={[0, 6, -5]} scale={[12, 3, 1]} color="#ffffff" />
            <Lightformer intensity={3.5} position={[-6, 4, 0]} scale={[3, 10, 1]} color="#C9971B" />
            <Lightformer intensity={4.0} position={[6, 4, 0]} scale={[3, 10, 1]} color="#1E3A7B" />
          </group>
        </Environment>
      </Canvas>
    </div>
  );
};
